import ConfirmDialog from '@components/Common/ConfirmDialog';
import { SectionCard, WorkspaceField } from '@components/Common/EntityWorkspace';
import MetricTable from '@components/Common/MetricTable';
import type { MetricColumn } from '@components/Common/MetricTable';
import SelectFormField from '@components/Common/SelectFormField';
import { backfillJobSeekerProfilesMutation } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import { Box, Button } from '@mui/material';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import React, { useState } from 'react';
import './index.scss';

interface KV { key: string; value: string }

interface Report {
  dryRun: boolean;
  scanned: number;
  updated: number;
  unchanged: number;
  failed: number;
  remaining: number;
  fieldsFilled: KV[];
  failures: KV[];
}

/** API field paths as an HR reads them. Anything unlisted shows its path, which is still legible. */
const FIELD_LABELS: Record<string, string> = {
  workHistory: 'Work history',
  educationEntries: 'Qualifications',
  certifications: 'Certificates',
  languages: 'Languages',
  skills: 'Skills',
  overview: 'Summary',
  currentCompany: 'Current employer',
  currentRole: 'Current designation',
  currentLocation: 'Current location',
  currentSalary: 'Current CTC (as written)',
  currentSalaryAmount: 'Current pay',
  currentSalaryCurrency: 'Current pay currency',
  currentSalaryFrequency: 'Current pay period',
  experience: 'Experience summary',
  yearsOfExperience: 'Years of experience',
  industry: 'Industry',
  subIndustry: 'Sub industry',
  education: 'Education (one line)',
  'jobPreferences.noticePeriod': 'Notice period',
  'jobPreferences.noticePeriodDays': 'Notice (days)',
  'jobPreferences.salaryExpectationMin': 'Expected pay (min)',
  'jobPreferences.salaryExpectationMax': 'Expected pay (max)',
  'jobPreferences.salaryExpectationCurrency': 'Expected pay currency',
  'jobPreferences.salaryExpectationFrequency': 'Expected pay period',
  'personalDetails.city': 'City',
  'personalDetails.state': 'State',
  'personalDetails.country': 'Country',
  'personalDetails.address': 'Street address',
  'personalDetails.gender': 'Gender',
};

const BATCH_SIZES = [10, 25, 50, 100].map((n) => ({ value: n, label: `${n} candidates` }));

const FIELD_COLUMNS: MetricColumn<KV>[] = [
  { key: 'field', header: 'Field', variant: 'name', render: (r) => FIELD_LABELS[r.key] ?? r.key, sortValue: (r) => FIELD_LABELS[r.key] ?? r.key },
  { key: 'count', header: 'Candidates', render: (r) => r.value, sortValue: (r) => Number(r.value) },
];

const FAILURE_COLUMNS: MetricColumn<KV>[] = [
  { key: 'id', header: 'Job seeker', variant: 'name', render: (r) => r.key },
  { key: 'reason', header: 'Why', align: 'left', render: (r) => r.value },
];

/**
 * Fill empty profile fields on existing candidates from the CV already on their record.
 *
 * Super Admin only, and the parent renders it only for one; the API refuses anyone else anyway.
 * A preview comes first on purpose: it costs the same OpenAI calls as a real run but writes
 * nothing, so the numbers can be read before a single record changes. The real run asks for
 * confirmation, because it writes to up to 100 records at a time.
 */
const ProfileBackfillCard: React.FC = () => {
  /*
   * Gated here, not only by the page that renders it. This is a bulk rewrite of candidate
   * profiles that spends money at OpenAI; a gate that lives in the parent is one refactor away
   * from handing it to every HR, with nothing failing.
   */
  const superAdmin = isSuperAdmin(useCurrentUserRole());
  const [limit, setLimit] = useState(25);
  const [running, setRunning] = useState<'preview' | 'fill' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'preview' : 'fill');
    setError(null);
    try {
      const res = await fetchGraphQLApiData(backfillJobSeekerProfilesMutation(dryRun, limit), {
        showStatus: !dryRun,
        message: 'Profile backfill',
      });
      setReport(res?.data?.backfillJobSeekerProfiles ?? null);
    } catch {
      setError('The backfill did not run. Nothing was changed. Try a smaller batch.');
    } finally {
      setRunning(null);
      setConfirmOpen(false);
    }
  };

  if (!superAdmin) return null;

  return (
    <SectionCard title="Fill profiles from CVs we already have" icon={<AutoFixHighOutlinedIcon />}>
      <Box className="cc-field--full cc-backfill">
        <p className="cc-backfill__intro">
          Reads each candidate&apos;s CV and fills work history, qualifications, certificates, pay and
          other fields that are empty. It never overwrites anything already entered, and never changes
          a name, email or phone. Run a preview first.
        </p>

        <Box className="cc-backfill__controls">
          <Box className="cc-backfill__limit">
            <SelectFormField
              required={false}
              name="backfillLimit"
              label="Batch size"
              value={limit}
              options={BATCH_SIZES}
              onChange={(_n, v) => setLimit(Number(v))}
            />
          </Box>
          <Button variant="outlined" onClick={() => run(true)} disabled={running !== null}>
            {running === 'preview' ? 'Previewing…' : 'Preview'}
          </Button>
          <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={running !== null}>
            {running === 'fill' ? 'Filling…' : 'Fill profiles'}
          </Button>
        </Box>

        {error && <p className="cc-backfill__error" role="alert">{error}</p>}

        {report && (
          <Box className="cc-backfill__report" aria-live="polite">
            <p className="cc-backfill__mode">
              {report.dryRun ? 'Preview: nothing was written.' : 'Done. These records were updated.'}
            </p>
            <Box className="cc-backfill__stats">
              <WorkspaceField label="Read" value={String(report.scanned)} mono />
              <WorkspaceField label={report.dryRun ? 'Would fill' : 'Filled'} value={String(report.updated)} mono />
              <WorkspaceField label="Nothing to add" value={String(report.unchanged)} mono />
              <WorkspaceField label="Could not read" value={String(report.failed)} mono />
              <WorkspaceField label="Still to do" value={String(report.remaining)} mono />
            </Box>
            {report.fieldsFilled.length > 0 && (
              <MetricTable<KV>
                columns={FIELD_COLUMNS}
                rows={report.fieldsFilled}
                getRowKey={(r) => r.key}
                defaultSort={{ key: 'count', dir: 'desc' }}
              />
            )}
            {report.failures.length > 0 && (
              <MetricTable<KV> columns={FAILURE_COLUMNS} rows={report.failures} getRowKey={(r) => r.key} />
            )}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Fill candidate profiles?"
        body={`This reads up to ${limit} CVs and writes the fields they add to those candidates. Nothing already entered is changed, and each candidate is only ever processed once.`}
        actionLabel="Fill profiles"
        busy={running === 'fill'}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => run(false)}
      />
    </SectionCard>
  );
};

export default ProfileBackfillCard;
